// © 2026 AttainIP Engineering
//
// Admin-portal user preferences that persist across reloads and re-logins.
// Backed by localStorage so the operator's view choices (e.g. hiding
// internal @attainip.ai accounts) survive a hard refresh and JWT
// expiry without having to be re-toggled.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'attain-admin-prefs/v1';

/**
 * The list of internal accounts now lives on the backend, set via the
 * ``ATTAIN_CFG_INTERNAL_EMAIL_RULES`` env var on attain-ss-api and
 * attain-api-identity (RFC-0121 §A3.1.5). The UI sends a single
 * boolean (``hide_internal``) and the backend resolves it against
 * its configured rules.
 */

export interface AdminPrefs {
  /** Hide users (and their data) flagged as internal by the backend.
   *  Default true — the operator usually wants a "customer view"
   *  first, with an explicit toggle to surface internal noise. */
  hideInternal: boolean;
}

interface AdminPrefsCtx extends AdminPrefs {
  setHideInternal: (v: boolean) => void;
}

const DEFAULT_PREFS: AdminPrefs = { hideInternal: true };

function loadPrefs(): AdminPrefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    return {
      hideInternal:
        typeof parsed?.hideInternal === 'boolean'
          ? parsed.hideInternal
          : DEFAULT_PREFS.hideInternal,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: AdminPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore quota / privacy errors — preferences are nice-to-have
  }
}

const AdminPrefsContext = createContext<AdminPrefsCtx | null>(null);

export function AdminPrefsProvider({ children }: { children: ReactNode }) {
  const [hideInternal, setHideInternalState] = useState<boolean>(
    () => loadPrefs().hideInternal,
  );

  // Persist whenever the value changes.
  useEffect(() => {
    savePrefs({ hideInternal });
  }, [hideInternal]);

  const setHideInternal = useCallback((v: boolean) => {
    setHideInternalState(v);
  }, []);

  const value = useMemo<AdminPrefsCtx>(
    () => ({
      hideInternal,
      setHideInternal,
    }),
    [hideInternal, setHideInternal],
  );

  return (
    <AdminPrefsContext.Provider value={value}>
      {children}
    </AdminPrefsContext.Provider>
  );
}

export function useAdminPrefs(): AdminPrefsCtx {
  const ctx = useContext(AdminPrefsContext);
  if (!ctx) {
    throw new Error('useAdminPrefs must be used inside <AdminPrefsProvider>');
  }
  return ctx;
}
